import React from 'react';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { REST_API, STATE_STATUS } from '@awp/constants';
import { GetJson, PutJson } from '@awp/helpers/fetchReact';
import { SENSOR_PLACEMENT_CONFIG } from '@awp/constants';
import { toNumberOrNull, isValueEmpty, isJsonValueEmpty } from '@awp/helpers/utilsReact';
import AwpToastContent from '@components/Common/AwpToastContent';
import { ToastContainer, toast } from 'react-toastify';
let initialState = {
  status: STATE_STATUS.IDLE,
  editing: null,
  data: {},
  observationTypes: [],
  template: null,
  orignTemplate: null,
  saveTemplateStatus: STATE_STATUS.IDLE,
  saveTemplateError: null,
  totId: SENSOR_PLACEMENT_CONFIG.TOT_ID,
};

export const getCurrentPlacementTemplatePacket = (state) => state.placementTemplatePacket.data;
export const getSelectedTemplate = (state) => state.placementTemplatePacket.template;
export const getOrignTemplate = (state) => state.placementTemplatePacket.orignTemplate;
export const getObservationTypes = (state) => state.placementTemplatePacket.observationTypes;
export const getNewTotId = (state) => state.placementTemplatePacket.totId;
export const isEditing = (state) => state.placementTemplatePacket.editing;
export const isLoading = (state) => state.placementTemplatePacket.status === STATE_STATUS.LOADING;
export const hasLoaded = (state) =>
  state.placementTemplatePacket.status === STATE_STATUS.SUCCESS ||
  state.placementTemplatePacket.status === STATE_STATUS.FAILED;
export const getSaveTemplateStatus = (state) => state.placementTemplatePacket.saveTemplateStatus;
export const getSaveTemplateError = (state) => state.placementTemplatePacket.saveTemplateError;

export const fetchPlacementTemplatePacket = createAsyncThunk(
  'placementTemplatePacket/fetchPlacementTemplatePacket',
  async (sensorTypeId, { getState }) => {
    const url = REST_API.PLACEMENT_TEMPLATES + 'placementTemplatePacket/' + sensorTypeId;
    const res = await GetJson(url);
    const ptmpPacket = await res.json();
    return ptmpPacket;
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (isLoading(state)) return false;
      return true;
    },
  },
);

export const saveTemplate = createAsyncThunk('placementTemplatePacket/saveTemplate', async (payload, { getState }) => {
  const state = getState();
  const url = REST_API.PLACEMENT_TEMPLATE;
  const resp = await PutJson(url, state.placementTemplatePacket.template);
  if (resp.ok) {
    return { response: await resp.json() };
  }
  return { response: null, error: await resp.json() };
});

export const placementTemplatePacketSlice = createSlice({
  name: 'placementTemplatePacket',
  initialState,
  reducers: {
    setEditing(state, action) {
      //editing: template+id
      return { ...state, editing: action.payload };
    },
    setData(state, action) {
      return { ...state, data: action.payload };
    },
    setObservationTypes(state, action) {
      return { ...state, observationTypes: action.payload };
    },
    setTemplate(state, action) {
      return { ...state, template: action.payload };
    },
    setOrignTemplate(state, action) {
      return { ...state, orignTemplate: action.payload };
    },
    setTotId(state, action) {
      return { ...state, totId: action.payload };
    },

    setSaveTemplateStatus(state, action) {
      return { ...state, saveTemplateStatus: action.payload };
    },
    setSaveTemplateError(state, action) {
      return { ...state, saveTemplateError: action.payload };
    },
  },
  extraReducers: {
    [fetchPlacementTemplatePacket.pending.toString()]: (state) => {
      state.status = STATE_STATUS.LOADING;
    },
    [fetchPlacementTemplatePacket.fulfilled.toString()]: (state, { payload }) => {
      state.status = STATE_STATUS.SUCCESS;
      state.data = payload;
      const _data = { ...state.data };
      const _ots = [..._data.ObservationTypes];
      let _allObservationTypeOptions = [];
      _ots.map((ot) => {
        _allObservationTypeOptions.push({
          value: ot.ObservationTypeId,
          label: ot.ObservationTypeName,
          observationTypeDisplayName: ot.ObservationTypeDisplayName,
        });
      });
      state.observationTypes = _allObservationTypeOptions;
    },
    [fetchPlacementTemplatePacket.rejected.toString()]: (state) => {
      state.status = STATE_STATUS.FAILED;
    },

    [saveTemplate.pending]: (state) => {
      state.saveTemplateStatus = STATE_STATUS.LOADING;
    },
    [saveTemplate.fulfilled]: (state, action) => {
      const { response, error } = action.payload;
      if (!response) {
        state.saveTemplateStatus = STATE_STATUS.FAILED;
        if (error.entity) {
          state.saveTemplateError = { ...error };
          toast(<AwpToastContent title={'Could not save placement template !'} msgList={error.entity} />, {
            autoClose: false,
          });
          return;
        }
        if (error) {
          state.saveTemplateError = { ...error };
          toast.error('Could not save placement template ! ', {
            autoClose: false,
          });
        }
        return;
      }
      state.saveTemplateStatus = STATE_STATUS.SUCCESS;
      let savedTemplate = { ...response };
      let _data = { ...state.data };

      let _placementTemplates = _data.PlacementTemplates;
      let _newPlacementTemplates = [];
      _placementTemplates.map((tpl) => {
        let _tpl = { ...tpl };
        if (_tpl.Id === SENSOR_PLACEMENT_CONFIG.TPL_ID || _tpl.Id === savedTemplate.Id) {
          _newPlacementTemplates.push(savedTemplate);
        } else {
          _newPlacementTemplates.push(_tpl);
        }
      });
      _data.PlacementTemplates = _newPlacementTemplates;
      state.data = _data;

      //cleanData
      state.editing = null;
      state.orignTemplate = null;
      state.template = null;
      state.saveTemplateError = null;
      toast.success('Placment template successfully saved!');
    },
    [saveTemplate.rejected]: (state, action) => {
      state.saveTemplateStatus = STATE_STATUS.FAILED;
    },
  },
});

export const {
  setData,
  setEditing,
  setTemplate,
  setOrignTemplate,
  setTotId,
  setSaveTemplateStatus,
  setSaveTemplateError,
} = placementTemplatePacketSlice.actions;
